<template>
  <div :class="['shift-calendar', `shift-calendar--${mode}`]">
    <el-calendar v-model="calendarDate">
      <template #header>
        <div class="shift-calendar-header">
          <div class="shift-calendar-title">{{ calendarTitle }}</div>
          <div class="shift-calendar-nav">
            <button type="button" :class="['shift-calendar-nav-btn', { 'is-active': calendarNavState === 'prev' }]" @click="goToPreviousMonth">
              Предыдущий месяц
            </button>
            <button type="button" :class="['shift-calendar-nav-btn', { 'is-active': calendarNavState === 'today' }]" @click="goToToday">
              Сегодня
            </button>
            <button type="button" :class="['shift-calendar-nav-btn', { 'is-active': calendarNavState === 'next' }]" @click="goToNextMonth">
              Следующий месяц
            </button>
          </div>
        </div>
      </template>
      <template #date-cell="{ data }">
        <div class="calendar-date-click-area">
          <div class="calendar-popover-trigger-wrapper">
            <template v-if="mode === 'view'">
              <el-popover
                v-if="hasShift(data.date)"
                placement="top"
                trigger="manual"
                :width="popoverWidth"
                :visible="activeDate === formatDate(data.date)"
                :teleported="!isCompactMobile"
                :show-arrow="false"
                :popper-style="dayPopoverStyle"
                @after-leave="activeDate = null"
                popper-class="shift-day-popover">
                <template #reference>
                  <div
                    class="calendar-popover-trigger calendar-date-click-area"
                    :class="{
                      active: activeDate === formatDate(data.date),
                      selected: activeDate === formatDate(data.date)
                    }"
                    @click.stop="onViewDayClick(data)"
                  >
                    <div class="calendar-date-inner">
                      <div class="cell-date">{{ data.date.getDate() }}</div>
                      <div v-if="getShiftTime(data.date)" class="cell-shift-inline">
                        {{ getShiftTime(data.date) }}
                      </div>
                    </div>
                  </div>
                </template>

                <div class="shift-day-popover-card">
                  <div class="shift-day-popover-title">Режим</div>
                  <div class="shift-day-popover-time">
                    <el-icon class="popover-clock-icon"><component :is="Clock" /></el-icon>
                    <span>{{ getShiftTime(data.date) }}</span>
                  </div>
                  <div v-if="getBreaks(data.date).length" class="shift-day-popover-breaks">
                    <div
                      v-for="(br, idx) in getBreaks(data.date)"
                      :key="idx"
                      class="shift-day-popover-break-row"
                    >
                      <span class="shift-day-popover-break-label">
                        {{ (br.start_time || br.start)?.slice(0, 5) }}–{{ (br.end_time || br.end)?.slice(0, 5) }}
                      </span>
                    </div>
                  </div>
                </div>
              </el-popover>

              <div v-else class="calendar-popover-trigger">
                <div class="calendar-date-inner">
                  <div class="cell-date">{{ data.date.getDate() }}</div>
                </div>
              </div>
            </template>

            <template v-else-if="mode === 'edit'">
              <el-popover
                v-if="isSelected(data.date)"
                placement="top"
                trigger="manual"
                :width="popoverWidth"
                :visible="activeDate === formatDate(data.date)"
                :teleported="!isCompactMobile"
                :show-arrow="false"
                :popper-style="dayPopoverStyle"
                @after-leave="activeDate = null"
                popper-class="shift-day-popover"
                :class="{ disabled: !workingHours && !unrestrictedHours }">
                <template #reference>
                  <div class="calendar-date-click-area"
                       :class="{
                         selected: isSelected(data.date),
                         active: activeDate === formatDate(data.date),
                         disabled: (!workingHours && !unrestrictedHours) || isPast(data.date)
                       }"
                       @click.stop="onDayClick(data)">
                    <div class="calendar-date-inner">
                      <div class="cell-date">{{ data.date.getDate() }}</div>
                      <div v-if="getShiftTime(data.date)" class="cell-shift-inline">
                        {{ getShiftTime(data.date) }}
                      </div>
                    </div>
                  </div>
                </template>
                <div class="shift-day-popover-card">
                  <div class="shift-day-popover-title">Режим</div>
                  <div class="shift-day-popover-time">
                    <el-icon class="popover-clock-icon"><component :is="Clock" /></el-icon>
                    <span>{{ getShiftTime(data.date) }}</span>
                  </div>
                  <div v-if="getBreaks(data.date).length" class="shift-day-popover-breaks">
                    <div
                      v-for="(br, idx) in getBreaks(data.date)"
                      :key="idx"
                      class="shift-day-popover-break-row"
                    >
                      <span class="shift-day-popover-break-label">
                        {{ (br.start_time || br.start)?.slice(0, 5) }}–{{ (br.end_time || br.end)?.slice(0, 5) }}
                      </span>
                      <el-button
                        class="shift-day-popover-break-remove"
                        size="small"
                        :icon="Delete"
                        @click="removeBreak(activeShift, idx)"
                      />
                    </div>
                  </div>
                  <div class="shift-day-popover-actions">
                    <el-tooltip content="Изменить время" placement="top">
                      <el-button class="shift-day-popover-action" size="small" :icon="Edit" @click="openEditShift(activeShift)"/>
                    </el-tooltip>
                    <el-tooltip content="Добавить перерыв" placement="top">
                      <el-button class="shift-day-popover-action" size="small" :icon="Plus" @click="openBreakDialog(activeShift)"/>
                    </el-tooltip>
                    <el-tooltip content="Удалить смену" placement="top">
                      <el-button class="shift-day-popover-action is-danger" size="small" :icon="Delete" @click="removeShift(formatDate(data.date))"/>
                    </el-tooltip>
                  </div>
                </div>
              </el-popover>
              <div v-else class="calendar-date-click-area"
                   :class="{ disabled: (!workingHours && !unrestrictedHours) || isPast(data.date) }"
                   @click.stop="onDayClick(data)">
                <div class="calendar-date-inner">
                  <div class="cell-date">{{ data.date.getDate() }}</div>
                  <div v-if="getShiftTime(data.date)" class="cell-shift-inline">
                    {{ getShiftTime(data.date) }}
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>
    </el-calendar>

    <el-dialog title="Изменить время смены" v-model="editDialogVisible" width="400px">
      <div style="display:flex; gap: 12px;">
        <el-time-select
          v-model="editStart"
          placeholder="Начало"
          step="00:15"
          :start="minWorkingHour"
          :end="maxWorkingHour"
          :max-time="editEnd || maxWorkingHour"
        />
        <el-time-select
          v-model="editEnd"
          placeholder="Конец"
          step="00:15"
          :start="minWorkingHour"
          :end="maxWorkingHour"
          :min-time="editStart || minWorkingHour"
        />
      </div>
      <template #footer>
        <el-button @click="editDialogVisible = false">Отмена</el-button>
        <el-button type="primary" @click="saveEditShift">Сохранить</el-button>
      </template>
    </el-dialog>

    <el-dialog title="Добавить перерыв" v-model="breakDialogVisible" width="400px">
      <el-form label-width="120px">
        <el-form-item label="Тип">
          <el-radio-group v-model="breakType">
            <el-radio-button label="lunch">🍽️</el-radio-button>
            <el-radio-button label="break">⏸️</el-radio-button>
            <el-radio-button label="maintenance">🛠️</el-radio-button>
            <el-radio-button label="handover">🔁</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="Начало">
          <el-select v-model="breakStart" placeholder="Начало" filterable style="width: 100%">
            <el-option
              v-for="opt in timeOptions"
              :key="'s-' + opt"
              :label="opt"
              :value="opt"
              :disabled="isStartDisabled(opt)"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Конец">
          <el-select v-model="breakEnd" placeholder="Конец" filterable style="width: 100%">
            <el-option
              v-for="opt in timeOptions"
              :key="'e-' + opt"
              :label="opt"
              :value="opt"
              :disabled="isEndDisabled(opt)"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="breakDialogVisible = false">Отмена</el-button>
        <el-button type="primary" @click="saveBreak">Сохранить</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { Delete, Edit, Plus, Clock } from '@element-plus/icons-vue'

export default {
  name: 'ShiftCalendar',
  computed: {
    Clock() {
      return Clock
    },
    Delete() {
      return Delete
    },
    Plus() {
      return Plus
    },
    Edit() {
      return Edit
    },
    minWorkingHour() {
      if (this.unrestrictedHours) return '00:00'
      return this.workingHours ? (this.workingHours.start_time || this.workingHours.start) : '00:00'
    },
    maxWorkingHour() {
      if (this.unrestrictedHours) return '23:45'
      return this.workingHours ? (this.workingHours.end_time || this.workingHours.end) : '23:59'
    },
    disabledBreakTimes() {
      if (!this.breakShift?.breaks) return []
      return this.breakShift.breaks.map(b => [b.start, b.end])
    },
    timeOptions() {
      const res = []
      const start = (this.minWorkingHour || '00:00').slice(0,5)
      const end = (this.maxWorkingHour || '23:59').slice(0,5)
      const toMin = t => {
        const [h,m] = t.split(':').map(Number); return h*60+m
      }
      const toStr = m => `${String(Math.floor(m/60)).padStart(2,'0')}:${String(m%60).padStart(2,'0')}`
      let cur = toMin(start)
      const stop = toMin(end)
      while (cur <= stop) { res.push(toStr(cur)); cur += 15 }
      return res
    },
    isCompactMobile() {
      return typeof window !== 'undefined' && window.innerWidth <= 480
    },
    popoverWidth() {
      return this.isCompactMobile ? 150 : 160
    },
    dayPopoverStyle() {
      return {
        '--el-popover-padding': '0px',
        padding: '0',
        background: 'transparent',
        border: 'none',
        boxShadow: 'none'
      }
    },
    calendarTitle() {
      const formatter = new Intl.DateTimeFormat('ru-RU', {
        month: 'long',
        year: 'numeric'
      })
      const parts = formatter.formatToParts(this.calendarDate)
      const month = parts.find(part => part.type === 'month')?.value || ''
      const year = parts.find(part => part.type === 'year')?.value || ''
      return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${year}`
    }
  },
  props: {
    unrestrictedHours: { type: Boolean, default: false },
    shifts: {
      type: Array,
      required: true,
    },
    mode: {
      type: String,
      default: 'view',
      validator(value) {
        return ['view', 'edit'].includes(value);
      }
    },
    workingHours: {
      type: Object,
      required: false,
      default: null,
    },
    selectedDates: {
      type: Array,
      required: false,
      default: () => [],
    },
    userName: { type: String, default: '' }
  },
  emits: ['update:selectedDates', 'update:shifts'],
  data() {
    return {
      calendarDate: new Date(),
      activeShift: null,
      activeDate: null,
      internalShifts: [],
      editDialogVisible: false,
      editShift: null,
      editStart: '',
      editEnd: '',
      breakDialogVisible: false,
      breakShift: null,
      breakType: 'lunch',
      breakStart: '',
      breakEnd: '',
      calendarNavState: ''
    }
  },
  watch: {
    shifts: {
      immediate: true,
      deep: true,
      handler (val) {
        this.internalShifts = (Array.isArray(val) ? val : []).map(shift => {
          const start = shift.start_time || shift.start
          const end = shift.end_time || shift.end

          return {
            ...shift,
            start_time: start,
            end_time: end,
            start,
            end,
            breaks: Array.isArray(shift.breaks)
              ? shift.breaks.map(b => {
                  const bStart = b.start_time || b.start
                  const bEnd = b.end_time || b.end
                  return {
                    ...b,
                    start: bStart,
                    end: bEnd
                  }
                })
              : []
          }
        })
      }
    },
    calendarDate() {
      this.activeDate = null
      this.activeShift = null
    },
    '$route.fullPath'() {
      this.closeActivePopover()
    }
  },
  beforeUnmount() {
    document.removeEventListener('pointerdown', this.handleDocumentPointerDown, true)
    window.removeEventListener('resize', this.closeActivePopover)
    window.removeEventListener('scroll', this.closeActivePopover, true)
    this.activeDate = null
    this.activeShift = null
  },
  mounted() {
    document.addEventListener('pointerdown', this.handleDocumentPointerDown, true)
    window.addEventListener('resize', this.closeActivePopover)
    window.addEventListener('scroll', this.closeActivePopover, true)
  },
  methods: {
    closeActivePopover() {
      this.activeDate = null
      this.activeShift = null
    },
    handleDocumentPointerDown(event) {
      const target = event?.target
      if (!target || typeof target.closest !== 'function') return
      if (
        target.closest('.shift-day-popover') ||
        target.closest('.calendar-popover-trigger') ||
        target.closest('.calendar-date-click-area')
      ) {
        return
      }
      this.closeActivePopover()
    },
    isStartDisabled(t) {
      const time = t.slice(0, 5)
      // запрещаем ставить начало внутри существующих перерывов
      if ((this.disabledBreakTimes || []).some(([s, e]) => time >= s && time < e)) return true
      // запрещаем если конец выбран раньше
      if (this.breakEnd && time >= this.breakEnd) return true
      return false
    },
    isEndDisabled(t) {
      const time = t.slice(0, 5)
      // конец не может быть внутри существующих перерывов
      if ((this.disabledBreakTimes || []).some(([s, e]) => time > s && time <= e)) return true
      // конец не может быть раньше начала
      if (!this.breakStart) return true
      if (time <= this.breakStart) return true
      return false
    },
    formatDate(date) {
      return date.getFullYear() + '-' +
             String(date.getMonth() + 1).padStart(2, '0') + '-' +
             String(date.getDate()).padStart(2, '0');
    },
    goToPreviousMonth() {
      this.calendarNavState = 'prev'
      this.calendarDate = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth() - 1, 1)
    },
    goToToday() {
      this.calendarNavState = 'today'
      this.calendarDate = new Date()
    },
    goToNextMonth() {
      this.calendarNavState = 'next'
      this.calendarDate = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth() + 1, 1)
    },
    hasShift(date) {
      const dStr = this.formatDate(date);
      return this.internalShifts.some(s => s.date === dStr);
    },
    getBreaks(date) {
      const dStr = this.formatDate(date);
      const shift = this.internalShifts.find(s => s.date === dStr);
      return shift && Array.isArray(shift.breaks) ? shift.breaks : [];
    },
    getShiftTime(date) {
      const dStr = this.formatDate(date);
      const shift = this.internalShifts.find(s => s.date === dStr);
      if (shift && shift.start_time && shift.end_time) {
        return `${shift.start_time.slice(0, 5)}-${shift.end_time.slice(0, 5)}`;
      }
      return null;
    },
    isSelected(date) {
      const dStr = this.formatDate(date);
      return this.internalShifts.some(s => s.date === dStr);
    },
    isPast(date) {
      const dStr = this.formatDate(date);
      const todayStr = this.formatDate(new Date());
      return dStr < todayStr;
    },
    onDayClick(data) {
      // data: { date: Date, type: string, ... }
      const panelYear = this.calendarDate.getFullYear();
      const panelMonth = this.calendarDate.getMonth();
      if (!this.workingHours && !this.unrestrictedHours) {
        this.$message?.warning?.('Сначала выберите локацию с рабочим режимом')
        return
      }
      const date = data.date;
      const dStr = this.formatDate(date);
      const todayStr = this.formatDate(new Date());
      if (dStr < todayStr) {
        this.$message?.warning?.('Нельзя создавать/редактировать смены в прошлом. Доступно с сегодняшнего дня');
        return;
      }
      const idx = this.internalShifts.findIndex(s => s.date === dStr);
      if (idx === -1) {
        // Первый клик: создаём смену и подсвечиваем день
        const newShift = {
          date: dStr,
          start_time: this.workingHours?.start_time || this.workingHours?.start || '09:00',
          end_time: this.workingHours?.end_time || this.workingHours?.end || '23:59',
          breaks: []
        };
        const newShifts = [...this.internalShifts, newShift];
        this.internalShifts = newShifts;
        this.$emit('update:shifts', newShifts);
        this.activeDate = dStr;
        this.activeShift = newShift;
      } else {
        // Повторный клик: открыть/закрыть поповер управления
        this.activeShift = this.internalShifts[idx];
        this.activeDate = (this.activeDate === dStr) ? null : dStr;
      }
      if (data.type === 'prev-month' || data.type === 'next-month') {
        this.$nextTick(() => {
          this.calendarDate = new Date(panelYear, panelMonth, 1);
        });
      }
    },
    onViewDayClick(data) {
      const dStr = this.formatDate(data.date)
      this.activeDate = this.activeDate === dStr ? null : dStr
      this.$emit('day-click', data)
    },
    removeShift(dStr) {
      const todayStr = this.formatDate(new Date());
      if (dStr < todayStr) {
        this.$message?.warning?.('Нельзя изменять прошедшие смены');
        return;
      }
      this.$confirm('Удалить эту смену?', 'Подтверждение', {
        confirmButtonText: 'Удалить',
        cancelButtonText: 'Отмена',
        type: 'warning',
      }).then(() => {
        const newShifts = this.internalShifts.filter(s => s.date !== dStr)
        this.internalShifts = newShifts
        this.$emit('update:shifts', newShifts)
        this.activeDate = null
      }).catch(() => {})
    },
    openEditShift(shift) {
      if (this.isPast(new Date(shift.date))) {
        this.$message?.warning?.('Нельзя редактировать прошедшие смены');
        return;
      }
      this.editShift = shift
      this.editStart = shift.start_time
      this.editEnd = shift.end_time
      this.editDialogVisible = true
    },
    saveEditShift() {
      if (!this.editShift) return
      this.editShift.start_time = this.editStart
      this.editShift.end_time = this.editEnd
      this.$emit('update:shifts', this.internalShifts)
      this.editDialogVisible = false
      this.editShift = null
    },
    openBreakDialog(shift) {
      if (this.isPast(new Date(shift.date))) {
        this.$message?.warning?.('Нельзя добавлять перерывы в прошедшие смены');
        return;
      }
      // Всегда берем свежую ссылку из internalShifts
      this.breakShift = this.internalShifts.find(s => s.date === shift.date) || shift
      this.breakType = 'lunch'
      this.breakStart = ''
      this.breakEnd = ''
      this.breakDialogVisible = true
    },
    saveBreak() {
      if (!this.breakShift) return
      if (!this.breakStart || !this.breakEnd) {
        this.$message?.warning?.('Укажите время перерыва')
        return
      }

      // Нормализация времени к HH:MM
      const toHM = (t) => (t || '').slice(0, 5)
      const nStart = toHM(this.breakStart)
      const nEnd = toHM(this.breakEnd)

      // Проверка пересечения с существующими перерывами
      const existing = this.breakShift.breaks || []
      const overlaps = existing.some(b => {
        const startB = toHM(b.start || b.start_time)
        const endB = toHM(b.end || b.end_time)
        return (nStart < endB && nEnd > startB)
      })
      if (overlaps) {
        this.$message?.warning?.('Выбранное время пересекается с существующим перерывом')
        return
      }

      const newBreak = {
        date: this.breakShift.date,
        type: this.breakType,
        start: nStart,
        end: nEnd
      }

      const updatedShifts = this.internalShifts.map(s => {
        if (s.date === this.breakShift.date) {
          return {
            ...s,
            breaks: [...(s.breaks || []), newBreak]
          }
        }
        return s
      })

      this.internalShifts = updatedShifts
      this.$emit('update:shifts', updatedShifts)

      // Обновляем активную смену на свежую ссылку, чтобы следующее добавление видело актуальные перерывы
      this.activeShift = this.internalShifts.find(s => s.date === this.breakShift.date) || null

      this.breakDialogVisible = false
      this.breakShift = null
    },
    removeBreak(shift, idx) {
      this.$confirm('Удалить этот перерыв?', 'Подтверждение', {
        confirmButtonText: 'Удалить',
        cancelButtonText: 'Отмена',
        type: 'warning',
      }).then(() => {
        const updatedShifts = this.internalShifts.map(s => {
          if (s.date === shift.date) {
            const newBreaks = [...s.breaks]
            newBreaks.splice(idx, 1)
            return { ...s, breaks: newBreaks }
          }
          return s
        })
        this.internalShifts = updatedShifts
        this.$emit('update:shifts', updatedShifts)
      }).catch(() => {
      })
    }
  }
};
</script>
<style scoped>
.shift-calendar,
.shift-calendar * {
  font-family: var(--ui-font-family-base) !important;
}

.shift-calendar {
  --shift-calendar-header-gap: 6px;
  --shift-calendar-header-padding: 4px 0 8px;
  --shift-calendar-title-size: 42px;
  --shift-calendar-nav-gap: 10px;
  --shift-calendar-nav-font-size: 11px;
  --shift-calendar-nav-padding-bottom: 0px;
  --shift-calendar-weekday-font-size: 22px;
  --shift-calendar-weekday-padding-bottom: 8px;
  --shift-calendar-day-height: 44px;
  --shift-calendar-day-font-size: 23px;
  --shift-calendar-day-top-padding: 2px;
  --shift-calendar-day-top-padding-selected: 1px;
  --shift-calendar-selected-size: 34px;
  --shift-calendar-selected-font-size: 18px;
  --shift-calendar-shift-font-size: 8px;
  --shift-calendar-shift-margin-top: 0px;
  width: 100%;
}

.shift-calendar--view {
  --shift-calendar-header-gap: 10px;
  --shift-calendar-header-padding: 8px 0 10px;
  --shift-calendar-title-size: 37px;
  --shift-calendar-nav-gap: 10px;
  --shift-calendar-nav-font-size: 12px;
  --shift-calendar-nav-padding-bottom: 2px;
  --shift-calendar-weekday-font-size: 22px;
  --shift-calendar-weekday-padding-bottom: 8px;
  --shift-calendar-day-height: 62px;
  --shift-calendar-day-font-size: 32px;
  --shift-calendar-day-top-padding: 4px;
  --shift-calendar-day-top-padding-selected: 3px;
  --shift-calendar-selected-size: 46px;
  --shift-calendar-selected-font-size: 27px;
  --shift-calendar-shift-font-size: 11px;
  --shift-calendar-shift-margin-top: 3px;
}

.shift-calendar--edit {
  --shift-calendar-header-gap: 8px;
  --shift-calendar-header-padding: 2px 0 6px;
  --shift-calendar-title-size: 28px;
  --shift-calendar-nav-gap: 10px;
  --shift-calendar-nav-font-size: 11px;
  --shift-calendar-nav-padding-bottom: 0px;
  --shift-calendar-weekday-font-size: 18px;
  --shift-calendar-weekday-padding-bottom: 6px;
  --shift-calendar-day-height: 40px;
  --shift-calendar-day-font-size: 22px;
  --shift-calendar-day-top-padding: 2px;
  --shift-calendar-day-top-padding-selected: 0px;
  --shift-calendar-selected-size: 30px;
  --shift-calendar-selected-font-size: 17px;
  --shift-calendar-shift-font-size: 7px;
  --shift-calendar-shift-margin-top: 0px;
}

.shift-calendar-header {
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  gap: var(--shift-calendar-header-gap);
  width: 100%;
  flex-wrap: nowrap;
}

.shift-calendar-title {
  color: #269ff5 !important;
  font-size: var(--shift-calendar-title-size) !important;
  font-weight: 700 !important;
  line-height: 1;
  white-space: nowrap;
  flex: 0 1 auto;
  min-width: 0;
}

.shift-calendar-nav {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--shift-calendar-nav-gap);
  margin-left: auto;
  padding-bottom: var(--shift-calendar-nav-padding-bottom);
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
}

.shift-calendar-nav-btn {
  padding: 0;
  border: 0;
  background: transparent;
  color: #ffffff !important;
  font: inherit;
  font-size: var(--shift-calendar-nav-font-size) !important;
  font-weight: 300 !important;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none !important;
}

.shift-calendar-nav-btn.is-active {
  color: #22c0ff !important;
  text-decoration: underline !important;
  text-underline-offset: 4px !important;
}

.shift-calendar-nav-btn:hover,
.shift-calendar-nav-btn:focus,
.shift-calendar-nav-btn:focus-visible {
  color: #22c0ff !important;
}

::v-deep(.el-calendar) {
  background: transparent;
  border: 0;
  color: var(--cal-text-inverse);
  --el-calendar-cell-width: 72px;
}

::v-deep(.el-calendar__header) {
  padding: var(--shift-calendar-header-padding);
  border-bottom: 0;
  background: transparent;
}

::v-deep(.el-calendar__body) {
  padding: 0 !important;
  height: auto !important;
  background: transparent;
}

::v-deep(.el-calendar-table) {
  background: transparent;
  height: auto !important;
  table-layout: fixed;
}

::v-deep(.el-calendar-table thead),
::v-deep(.el-calendar-table tbody),
::v-deep(.el-calendar-table tr),
::v-deep(.el-calendar-table td) {
  background: transparent;
  height: auto !important;
}

::v-deep(.el-calendar-table td),
::v-deep(.el-calendar-table tr:first-child td),
::v-deep(.el-calendar-table td.is-selected),
::v-deep(.el-calendar-table td.next),
::v-deep(.el-calendar-table td.prev) {
  border: 0 !important;
  padding: 0 !important;
  overflow: visible !important;
  vertical-align: top !important;
}

.calendar-cell {
  max-height: 100px;
  overflow-y: auto;
}

.cell-break-time {
  line-height: 1.05;
  font-size: 10px;
}
.cell-shift-time {
  font-size: 11px;
  line-height: 1.2;
  font-weight: 500;
}
.cell-info {
  font-size: 10px;
  line-height: 1.1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.calendar-popover-trigger {
  cursor: pointer;
  user-select: none;
}
.calendar-date-click-area {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 100%;
  cursor: pointer;
  user-select: none;
}
.calendar-date-click-area.selected {
  color: var(--cal-text-inverse) !important;
}
.calendar-popover-trigger-wrapper {
  width: 100%;
  height: 100%;
}
.calendar-date-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  padding: var(--shift-calendar-day-top-padding) 0 0;
}
.cell-date {
  margin-bottom: 2px;
}

.calendar-date-click-area.selected .calendar-date-inner {
  background: transparent;
  border-radius: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  justify-content: flex-start;
  padding: var(--shift-calendar-day-top-padding-selected) 0 0;
}

/* Убираем стандартный hover эффект у el-calendar на уровне ячейки дня */
::v-deep(.el-calendar-day:hover) {
  background-color: transparent !important;
}

/* Убираем внутренние отступы у ячеек дня */
::v-deep(.el-calendar-day) {
  padding: 0 !important;
  display: flex !important;
  align-items: stretch !important;
  background: transparent !important;
  height: var(--shift-calendar-day-height) !important;
  min-height: var(--shift-calendar-day-height) !important;
}

/* Центрируем число и делаем его ровным */
.cell-date {
  display: flex !important;
  align-items: flex-start !important;
  justify-content: center !important;
  width: 100% !important;
  font-family: var(--ui-font-family-base) !important;
  font-weight: 300 !important;
  font-size: var(--shift-calendar-day-font-size) !important;
  line-height: 1 !important;
  color: #ffffff !important;
  text-shadow: none !important;
}

::v-deep(.el-calendar-table td.is-selected) {
  background-color: transparent;
}

::v-deep(.el-calendar-table td) {
  color: #eef5ff !important;
}

::v-deep(.el-calendar-table td .cell-date) {
  color: #eef5ff !important;
  font-weight: 400 !important;
}

::v-deep(.el-calendar-table td.prev),
::v-deep(.el-calendar-table td.next) {
  color: #24314d !important;
}

::v-deep(.el-calendar-table td.prev .cell-date),
::v-deep(.el-calendar-table td.next .cell-date) {
  color: #000000 !important;
  font-weight: 300 !important;
  text-shadow: none;
}

::v-deep(.el-calendar-table td:not(.prev):not(.next) .cell-date) {
  color: #eef5ff !important;
  font-weight: 300 !important;
}

::v-deep(.el-calendar-table td.is-today .cell-date) {
  color: #f7f3ff !important;
}

.calendar-date-click-area.selected .cell-date {
  color: #f7f3ff !important;
}

.calendar-date-click-area.active .cell-date {
  color: #22c0ff !important;
}

.calendar-date-click-area.selected .cell-date {
  background-color: #22c0ff !important;
  color: #ffffff !important;
  border-radius: 50% !important;
  width: var(--shift-calendar-selected-size) !important;
  height: var(--shift-calendar-selected-size) !important;
  line-height: var(--shift-calendar-selected-size) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin: 0 auto !important;
  font-weight: 400 !important;
  font-size: var(--shift-calendar-selected-font-size) !important;
}

.cell-shift-inline {
  margin-top: var(--shift-calendar-shift-margin-top) !important;
  color: #22c0ff !important;
  font-size: var(--shift-calendar-shift-font-size) !important;
  line-height: 1 !important;
  font-weight: 700 !important;
  white-space: nowrap !important;
}

.shift-day-popover-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 18px 22px 14px;
  text-align: center;
}

.shift-day-popover-card {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 14px 18px !important;
  text-align: center !important;
  background: #0c224b !important;
  border-radius: 10px !important;
  width: 100% !important;
  box-sizing: border-box !important;
}

.shift-day-popover-title {
  color: #ffffff !important;
  font-size: 22px !important;
  font-weight: 700 !important;
  line-height: 1.2 !important;
}

.shift-day-popover-time {
  display: flex !important;
  align-items: center !important;
  gap: 10px !important;
  margin-top: 10px !important;
  color: #22c0ff !important;
  font-size: 22px !important;
  font-weight: 700 !important;
  line-height: 1.2 !important;
}

.popover-clock-icon {
  font-size: 22px !important;
  color: #22c0ff !important;
}

.popover-clock-icon :deep(svg),
.popover-clock-icon :deep(svg path) {
  color: #22c0ff !important;
  fill: #22c0ff !important;
}

.shift-day-popover-breaks {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgba(102, 140, 214, 0.18);
}

.shift-day-popover-break-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.shift-day-popover-break-label {
  color: rgba(247, 243, 255, 0.88);
  font-size: 13px;
  line-height: 1;
}

.shift-day-popover-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  margin-top: 14px;
}

.shift-day-popover-action,
.shift-day-popover-break-remove {
  min-width: 36px !important;
  height: 36px !important;
  padding: 0 10px !important;
  background: rgba(23, 46, 101, 0.9) !important;
  border: 1px solid rgba(93, 126, 198, 0.3) !important;
  border-radius: 8px !important;
  color: #eaf6ff !important;
}

.shift-day-popover-action.is-danger,
.shift-day-popover-break-remove {
  color: #ff8b8b !important;
}

.shift-day-popover-action:hover,
.shift-day-popover-action:focus,
.shift-day-popover-break-remove:hover,
.shift-day-popover-break-remove:focus {
  background: rgba(29, 57, 121, 0.96) !important;
  border-color: rgba(102, 144, 229, 0.44) !important;
}

.cell-shift-tag, .cell-break-tag {
  margin-top: 3px;
  font-weight: 600;
  display: flex;
  text-align: center;
  font-size: 9px;
  justify-content: flex-start;
  align-items: center;
}

/* Подсветка выходных дней (суббота и воскресенье) */
::v-deep(.el-calendar-table colgroup col:nth-child(7)),
::v-deep(.el-calendar-table colgroup col:nth-child(6)) {
  background-color: transparent;
}

::v-deep(.el-calendar-table td:nth-child(7) .el-calendar-day),
::v-deep(.el-calendar-table td:nth-child(6) .el-calendar-day) {
  background-color: transparent;
}

::v-deep(.el-calendar-table th) {
  background: transparent;
  border-bottom: 1px solid rgba(135, 190, 255, 0.28) !important;
  color: #ffffff !important;
  font-size: var(--shift-calendar-weekday-font-size) !important;
  font-weight: 300 !important;
  line-height: 1 !important;
  padding-bottom: var(--shift-calendar-weekday-padding-bottom) !important;
  text-align: center !important;
  text-transform: capitalize !important;
}

::v-deep(.el-calendar-table th:nth-child(7)),
::v-deep(.el-calendar-table th:nth-child(6)) {
  background: transparent;
}

::v-deep(.el-popover) {
  max-width: 360px;
  white-space: normal;
}

::v-deep(.shift-day-popover.el-popper) {
  --el-popover-padding: 0 !important;
  --el-bg-color-overlay: transparent !important;
  --el-popover-border-color: transparent !important;
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  border: 0 !important;
  border-color: transparent !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  padding: 0 !important;
  outline: 0 !important;
}

::v-deep(.shift-day-popover),
::v-deep(.shift-day-popover.el-popper.is-light),
::v-deep(.shift-day-popover.el-popover.is-light),
::v-deep(.shift-day-popover.el-popover),
::v-deep(.shift-day-popover[data-popper-placement]) {
  --el-bg-color-overlay: transparent !important;
  --el-popover-border-color: transparent !important;
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  border: 0 !important;
  border-color: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  outline: 0 !important;
}

::v-deep(.shift-day-popover .el-popper__arrow),
::v-deep(.shift-day-popover.el-popper .el-popper__arrow:before) {
  background: #0c224b !important;
  border: 0 !important;
  box-shadow: none !important;
}

::v-deep(.shift-day-popover.el-popover),
::v-deep(.shift-day-popover .el-popover__content),
::v-deep(.shift-day-popover > .el-popper__arrow),
::v-deep(.shift-day-popover .el-popper__arrow::before) {
  background: transparent !important;
  border: 0 !important;
  padding: 0 !important;
  box-shadow: none !important;
}

.calendar-date-click-area.disabled {
  pointer-events: none;
  opacity: 0.4;
}

.cell-break-icons {
  display: flex;
  flex-direction: row;
  gap: 2px;
  margin-top: 2px;
  font-size: 12px;
}
.break-icon {
  line-height: 1;
}

::v-deep(.el-dialog) {
  background: var(--appointment-panel);
  border: 1px solid var(--cal-border);
}

::v-deep(.el-dialog__title),
::v-deep(.el-form-item__label) {
  color: var(--cal-text-inverse);
}

::v-deep(.el-time-select .el-input__wrapper),
::v-deep(.el-select__wrapper),
::v-deep(.el-radio-button__inner) {
  background: var(--appointment-control);
  color: var(--cal-text-inverse);
  box-shadow: 0 0 0 1px var(--cal-border) inset;
  border-color: transparent;
}

::v-deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
  background: var(--appointment-primary);
  color: var(--appointment-primary-text);
  box-shadow: 0 0 0 1px var(--cal-border) inset;
}

::v-deep(.el-dialog .el-button--default) {
  background: var(--appointment-secondary);
  border-color: transparent;
  color: var(--appointment-secondary-text);
}

::v-deep(.el-dialog .el-button--primary) {
  background: var(--appointment-primary);
  border-color: transparent;
  color: var(--appointment-primary-text);
}

/* Unified Calendar Styles for Edit & View Modes */
.shift-calendar--edit .shift-calendar-title,
.shift-calendar--view .shift-calendar-title {
  color: #269ff5 !important;
  font-size: var(--shift-calendar-title-size) !important;
  font-weight: 700 !important;
}

.shift-calendar--edit .shift-calendar-nav-btn,
.shift-calendar--view .shift-calendar-nav-btn {
  color: #ffffff !important;
  font-size: var(--shift-calendar-nav-font-size) !important;
  font-weight: 300 !important;
}

.shift-calendar--edit .shift-calendar-nav-btn.is-active,
.shift-calendar--view .shift-calendar-nav-btn.is-active,
.shift-calendar--edit .shift-calendar-nav-btn:hover,
.shift-calendar--view .shift-calendar-nav-btn:hover {
  color: #22c0ff !important;
}

.shift-calendar--edit ::v-deep(.el-calendar-table th),
.shift-calendar--view ::v-deep(.el-calendar-table th) {
  font-size: var(--shift-calendar-weekday-font-size) !important;
  font-weight: 400 !important;
  padding-bottom: var(--shift-calendar-weekday-padding-bottom) !important;
}

.shift-calendar--edit ::v-deep(.el-calendar-day),
.shift-calendar--view ::v-deep(.el-calendar-day) {
  height: var(--shift-calendar-day-height) !important;
  min-height: var(--shift-calendar-day-height) !important;
}

.shift-calendar--edit .cell-date,
.shift-calendar--view .cell-date {
  font-size: var(--shift-calendar-day-font-size) !important;
}

.shift-calendar--edit .cell-shift-inline,
.shift-calendar--view .cell-shift-inline {
  font-size: var(--shift-calendar-shift-font-size) !important;
  margin-top: var(--shift-calendar-shift-margin-top) !important;
}

.shift-calendar--edit .calendar-date-click-area.selected .cell-date,
.shift-calendar--view .calendar-date-click-area.selected .cell-date {
  width: var(--shift-calendar-selected-size) !important;
  height: var(--shift-calendar-selected-size) !important;
  line-height: var(--shift-calendar-selected-size) !important;
  font-size: var(--shift-calendar-selected-font-size) !important;
  background-color: #22c0ff !important;
  color: #ffffff !important;
  border-radius: 50% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin: 0 auto !important;
}

.shift-calendar--edit ::v-deep(.el-calendar-table td.prev .cell-date),
.shift-calendar--edit ::v-deep(.el-calendar-table td.next .cell-date),
.shift-calendar--view ::v-deep(.el-calendar-table td.prev .cell-date),
.shift-calendar--view ::v-deep(.el-calendar-table td.next .cell-date) {
  color: #091638 !important;
  opacity: 1 !important;
}

.shift-calendar--edit ::v-deep(.el-calendar-table td:not(.prev):not(.next) .cell-date),
.shift-calendar--view ::v-deep(.el-calendar-table td:not(.prev):not(.next) .cell-date) {
  color: #ffffff !important;
  opacity: 1 !important;
}

@media (max-width: 860px) {
  .shift-calendar-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    flex-wrap: nowrap;
  }

  .shift-calendar-nav {
    justify-content: flex-end !important;
    gap: 12px;
    margin-left: auto !important;
    white-space: nowrap;
    flex-wrap: nowrap;
    padding-bottom: 0;
    width: 100%;
  }

  .shift-calendar-title {
    font-size: 32px;
  }
}

@media (max-width: 640px) {
  .shift-calendar--view .cell-date {
    font-size: 24px !important;
  }
  .shift-calendar--view ::v-deep(.el-calendar-day) {
    height: 44px !important;
    min-height: 44px !important;
  }
  .shift-calendar--view ::v-deep(.el-calendar-table th) {
    font-size: 16px !important;
    padding-bottom: 6px !important;
  }
  .shift-calendar--view .cell-shift-inline {
    font-size: 8px !important;
  }
  .shift-calendar-nav-btn {
    font-size: 10px !important;
  }
  .shift-day-popover-card {
    padding: 8px 12px !important;
  }
  .shift-day-popover-title {
    font-size: 18px !important;
  }
  .shift-day-popover-time {
    font-size: 18px !important;
  }
}

@media (max-width: 480px) {
  .shift-calendar--edit {
    --shift-calendar-header-gap: 4px;
    --shift-calendar-header-padding: 0 0 3px;
    --shift-calendar-title-size: 24px;
    --shift-calendar-nav-gap: 8px;
    --shift-calendar-nav-font-size: 9px;
    --shift-calendar-nav-padding-bottom: 0px;
    --shift-calendar-weekday-font-size: 16px;
    --shift-calendar-weekday-padding-bottom: 4px;
    --shift-calendar-day-height: 32px;
    --shift-calendar-day-font-size: 16px;
    --shift-calendar-day-top-padding: 1px;
    --shift-calendar-day-top-padding-selected: 0px;
    --shift-calendar-selected-size: 26px;
    --shift-calendar-selected-font-size: 14px;
    --shift-calendar-shift-font-size: 7px;
    --shift-calendar-shift-margin-top: 0px;
  }

  .shift-calendar--view {
    --shift-calendar-header-gap: 5px;
    --shift-calendar-title-size: 22px;
    --shift-calendar-nav-gap: 7px;
    --shift-calendar-nav-font-size: 7px;
    --shift-calendar-weekday-font-size: 16px;
    --shift-calendar-day-height: 39px;
    --shift-calendar-day-font-size: 20px;
    --shift-calendar-selected-size: 32px;
    --shift-calendar-selected-font-size: 18px;
    --shift-calendar-shift-font-size: 8px;
    --shift-calendar-shift-margin-top: 2px;
    --shift-calendar-day-top-padding: 2px;
    --shift-calendar-day-top-padding-selected: 1px;
  }

  .shift-calendar-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
  .shift-calendar-title {
    font-size: 22px !important;
    line-height: 1 !important;
  }
  .shift-calendar-nav {
    gap: 10px !important;
    flex-wrap: nowrap !important;
    justify-content: flex-end !important;
    width: 100% !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .shift-calendar-nav::-webkit-scrollbar {
    display: none;
  }
  .shift-calendar-nav-btn {
    flex: 0 0 auto;
    font-size: 9px !important;
    letter-spacing: -0.1px;
  }
  .shift-calendar--view .cell-date {
    font-size: 18px !important;
  }
  .shift-calendar--edit ::v-deep(.el-calendar-day) {
    height: var(--shift-calendar-day-height) !important;
    min-height: var(--shift-calendar-day-height) !important;
  }
  .shift-calendar--view ::v-deep(.el-calendar-day) {
    height: var(--shift-calendar-day-height) !important;
    min-height: var(--shift-calendar-day-height) !important;
    overflow: visible !important;
  }
  .shift-calendar--view ::v-deep(.el-calendar-table th) {
    font-size: 12px !important;
    padding-bottom: 5px !important;
  }
  .shift-calendar--edit .calendar-date-click-area.selected .cell-date {
    width: var(--shift-calendar-selected-size) !important;
    height: var(--shift-calendar-selected-size) !important;
    line-height: var(--shift-calendar-selected-size) !important;
    font-size: var(--shift-calendar-selected-font-size) !important;
  }
  .shift-calendar--view .calendar-date-click-area.selected .cell-date {
    width: var(--shift-calendar-selected-size) !important;
    height: var(--shift-calendar-selected-size) !important;
    line-height: var(--shift-calendar-selected-size) !important;
    font-size: var(--shift-calendar-selected-font-size) !important;
  }
  .shift-calendar--edit .cell-shift-inline {
    display: none !important;
  }
  .shift-calendar--view .calendar-date-click-area.selected,
  .shift-calendar--view .calendar-date-click-area.selected .calendar-date-inner,
  .shift-calendar--view .calendar-popover-trigger-wrapper {
    overflow: visible !important;
  }
  .shift-calendar--view .calendar-date-click-area.selected .calendar-date-inner {
    position: relative !important;
    padding-top: 2px !important;
    padding-bottom: 16px !important;
  }
  .shift-calendar--view .calendar-date-click-area.selected .cell-shift-inline {
    display: block !important;
    margin: 0 !important;
    font-size: 7px !important;
    line-height: 1 !important;
    color: #22c0ff !important;
    opacity: 1 !important;
    visibility: visible !important;
    position: absolute !important;
    left: 50% !important;
    bottom: 2px !important;
    transform: translateX(-50%) !important;
    z-index: 1 !important;
    width: max-content !important;
    white-space: nowrap !important;
  }
  .shift-day-popover-card {
    background: #0c224b !important;
    padding: 10px 12px !important;
    border-radius: 8px !important;
  }
  .shift-day-popover-title {
    font-size: 16px !important;
  }
  .shift-day-popover-time {
    margin-top: 6px !important;
    font-size: 16px !important;
    gap: 8px !important;
  }
}

@media (max-width: 390px) {
  .shift-calendar--view {
    --shift-calendar-day-height: 36px;
    --shift-calendar-day-font-size: 18px;
    --shift-calendar-selected-size: 30px;
    --shift-calendar-selected-font-size: 16px;
  }

  .shift-calendar-title {
    font-size: 20px !important;
  }

  .shift-calendar-nav {
    gap: 6px !important;
  }

  .shift-calendar-nav-btn {
    font-size: 8px !important;
  }

  .shift-calendar--view .cell-date {
    font-size: 17px !important;
  }

  .shift-calendar--edit ::v-deep(.el-calendar-day) {
    height: var(--shift-calendar-day-height) !important;
    min-height: var(--shift-calendar-day-height) !important;
  }
  .shift-calendar--view ::v-deep(.el-calendar-day) {
    height: var(--shift-calendar-day-height) !important;
    min-height: var(--shift-calendar-day-height) !important;
  }

  .shift-calendar--edit .calendar-date-click-area.selected .cell-date {
    width: var(--shift-calendar-selected-size) !important;
    height: var(--shift-calendar-selected-size) !important;
    line-height: var(--shift-calendar-selected-size) !important;
    font-size: var(--shift-calendar-selected-font-size) !important;
  }

  .shift-calendar--view .calendar-date-click-area.selected .cell-date {
    width: var(--shift-calendar-selected-size) !important;
    height: var(--shift-calendar-selected-size) !important;
    line-height: var(--shift-calendar-selected-size) !important;
    font-size: var(--shift-calendar-selected-font-size) !important;
  }

  .shift-calendar--view .calendar-date-click-area.selected .cell-shift-inline {
    font-size: 7px !important;
    bottom: 2px !important;
  }
}

/* Final compact Figma popover for day shift details. */
.shift-day-popover-card {
  min-width: 160px !important;
  width: 160px !important;
  padding: 10px 12px 9px !important;
  align-items: center !important;
  justify-content: center !important;
  background:
    linear-gradient(rgba(2, 27, 70, 0.49), rgba(2, 27, 70, 0.49)),
    linear-gradient(299deg, #030b1b 13.22%, #15264e 86.78%) !important;
  border-radius: 3px !important;
  box-shadow: none !important;
  box-sizing: border-box !important;
}

::v-deep(.shift-day-popover .el-popper__arrow),
::v-deep(.shift-day-popover.el-popper .el-popper__arrow) {
  display: none !important;
}

::v-deep(.shift-day-popover.el-popper::after),
::v-deep(.shift-day-popover.el-popover::after) {
  content: "";
  position: absolute;
  left: 50%;
  width: 0;
  height: 0;
  transform: translateX(-50%);
  pointer-events: none;
}

.shift-day-popover-title {
  font-size: 13px !important;
  font-weight: 700 !important;
  line-height: 1 !important;
}

.shift-day-popover-time {
  margin-top: 8px !important;
  gap: 5px !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  color: #269ff5 !important;
}

.popover-clock-icon {
  font-size: 12px !important;
  color: #269ff5 !important;
}

::v-deep(.shift-day-popover.el-popper .el-popper__arrow::before),
::v-deep(.shift-day-popover .el-popper__arrow::before) {
  background:
    linear-gradient(rgba(2, 27, 70, 0.49), rgba(2, 27, 70, 0.49)),
    linear-gradient(299deg, #030b1b 13.22%, #15264e 86.78%) !important;
  border: 0 !important;
  width: 16px !important;
  height: 16px !important;
  box-shadow: none !important;
}

::v-deep(.shift-day-popover.el-popper),
::v-deep(.shift-day-popover.el-popover),
::v-deep(.shift-day-popover[data-popper-placement]) {
  width: 160px !important;
  min-width: 160px !important;
  max-width: 160px !important;
  overflow: visible !important;
}

</style>

<style>
.shift-day-popover.el-popper .el-popper__arrow,
.shift-day-popover.el-popover .el-popper__arrow {
  display: none !important;
}

.shift-day-popover.el-popper .el-popper__arrow::before,
.shift-day-popover.el-popover .el-popper__arrow::before {
  width: 16px !important;
  height: 16px !important;
  background:
    linear-gradient(rgba(2, 27, 70, 0.49), rgba(2, 27, 70, 0.49)),
    linear-gradient(299deg, #030b1b 13.22%, #15264e 86.78%) !important;
  border: 0 !important;
  box-shadow: none !important;
}

.shift-day-popover.el-popper[data-popper-placement^="top"] .el-popper__arrow {
  display: none !important;
}

.shift-day-popover.el-popper[data-popper-placement^="bottom"] .el-popper__arrow {
  display: none !important;
}

.shift-day-popover.el-popper,
.shift-day-popover.el-popover {
  overflow: visible !important;
}

.shift-day-popover.el-popper::after,
.shift-day-popover.el-popover::after {
  content: "";
  position: absolute;
  left: 50%;
  width: 0;
  height: 0;
  transform: translateX(-50%);
  pointer-events: none;
}

/* В режиме редактирования рабочие даты отмечены цветом числа. */
.shift-calendar--edit .calendar-date-click-area.selected .cell-date {
  color: #79e6b8 !important;
}

.shift-day-popover.el-popper[data-popper-placement^="top"]::after {
  bottom: -9px;
  border-left: 9px solid transparent;
  border-right: 9px solid transparent;
  border-top: 9px solid #06122c;
}

.shift-day-popover.el-popper[data-popper-placement^="bottom"]::after {
  top: -9px;
  border-left: 9px solid transparent;
  border-right: 9px solid transparent;
  border-bottom: 9px solid #06122c;
}
</style>
